import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";

export interface PulseEntry {
  id: number;
  project_id: string;
  author_email: string;
  author_name: string | null;
  body: string;
  created_at: string;
  edited_at: string | null;
}

export function usePulseEntries(projectId: string) {
  return useQuery<PulseEntry[]>({
    queryKey: ["pulse", projectId],
    queryFn: () => api.get(`/pulse/${projectId}`).then((r) => r.data),
    enabled: !!projectId,
  });
}

// Every mutation also invalidates ["portfolio"]: the backend mirrors
// the newest entry into projects.status_update, which the hero's
// LatestPulseLine and the staleness signal read.
function useInvalidatePulse(projectId: string) {
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries({ queryKey: ["pulse", projectId] });
    qc.invalidateQueries({ queryKey: ["portfolio"] });
  };
}

export function useAddPulseEntry(projectId: string) {
  const invalidate = useInvalidatePulse(projectId);
  return useMutation({
    mutationFn: (body: string) =>
      api.post(`/pulse/${projectId}`, { body }).then((r) => r.data),
    onSuccess: invalidate,
  });
}

export function useUpdatePulseEntry(projectId: string) {
  const invalidate = useInvalidatePulse(projectId);
  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: string }) =>
      api.patch(`/pulse/id/${id}`, { body }).then((r) => r.data),
    onSuccess: invalidate,
  });
}

export function useDeletePulseEntry(projectId: string) {
  const invalidate = useInvalidatePulse(projectId);
  return useMutation({
    mutationFn: (id: number) => api.delete(`/pulse/id/${id}`),
    onSuccess: invalidate,
  });
}
